import { useState } from "react";
import { Pencil, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { COBRANCA_LABELS, DIAS_DA_SEMANA, FREQUENCIA_LABELS, regraEfetiva, type RegraDeExtrato, type RegraDoClienteInput } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { campo } from "../estilos";
import { CamposDaCobranca } from "./CamposDaCobranca";

const VAZIA = { cobranca: null, frequencia: null, diaDeFechamento: null, diaDaSemana: null, prazoDeVencimentoDias: null };

function resumo(r: RegraDeExtrato) {
  const quando = r.frequencia === "MENSAL" ? (r.diaDeFechamento === 31 ? "último dia" : `dia ${r.diaDeFechamento}`) : r.frequencia === "SEMANAL" ? DIAS_DA_SEMANA[r.diaDaSemana]!.toLowerCase() : "";
  return [COBRANCA_LABELS[r.cobranca], `${FREQUENCIA_LABELS[r.frequencia]}${quando ? ` (${quando})` : ""}`, `vence em ${r.prazoDeVencimentoDias} dias`].join(" · ");
}

/** Os clientes que fogem do padrão — por exemplo, a clínica em que cada dentista paga o seu. */
export function RegrasDosClientes({ padrao, pode }: { padrao: RegraDeExtrato; pode: boolean }) {
  const utils = trpc.useUtils();
  const dados = trpc.financeiro.configExtrato.regras.useQuery().data;
  const [editando, setEditando] = useState<RegraDoClienteInput | null>(null);
  const invalidar = () => utils.financeiro.invalidate();
  const salvar = trpc.financeiro.configExtrato.salvarRegra.useMutation({
    onSuccess: () => {
      toast.success("Regra do cliente salva.");
      setEditando(null);
      invalidar();
    },
    onError: (e) => toast.error(e.message),
  });
  const remover = trpc.financeiro.configExtrato.removerRegra.useMutation({ onSuccess: invalidar, onError: (e) => toast.error(e.message) });

  const comRegra = new Set((dados?.regras ?? []).map((r) => r.clientOrganizationId));
  const livres = (dados?.clientes ?? []).filter((c) => !comRegra.has(c.id) || c.id === editando?.clientOrganizationId);

  return (
    <div className="border-t border-zinc-100 dark:border-border">
      <div className="flex items-center justify-between gap-2 px-4 py-3">
        <div>
          <p className="text-sm font-semibold text-zinc-900 dark:text-foreground">Clientes com regra própria</p>
          <p className="text-xs text-zinc-500 dark:text-muted-foreground">O que não for definido aqui segue o padrão acima.</p>
        </div>
        {pode && !editando && livres.length > 0 && (
          <button
            type="button"
            onClick={() => setEditando({ clientOrganizationId: livres[0]!.id, ...VAZIA })}
            className="inline-flex items-center gap-1 text-xs font-semibold text-brand-700 hover:underline dark:text-primary"
          >
            <Plus className="h-3.5 w-3.5" />
            Regra para um cliente
          </button>
        )}
      </div>

      {editando && (
        <div className="mx-4 mb-3 space-y-3 rounded-lg border border-zinc-200 p-3 dark:border-border">
          <select value={editando.clientOrganizationId} onChange={(e) => setEditando({ ...editando, clientOrganizationId: e.target.value })} aria-label="Cliente" className={campo}>
            {livres.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nome}
              </option>
            ))}
          </select>
          <CamposDaCobranca comPadrao valor={editando} padrao={padrao} onChange={(v) => setEditando({ ...editando, ...v })} />
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setEditando(null)} className="h-9 rounded-md px-3 text-sm text-zinc-600 hover:bg-zinc-100 dark:text-muted-foreground dark:hover:bg-muted">
              Cancelar
            </button>
            <button
              type="button"
              disabled={salvar.isPending}
              onClick={() => salvar.mutate(editando)}
              className="h-9 rounded-md bg-brand-600 px-3 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:opacity-50"
            >
              Salvar regra
            </button>
          </div>
        </div>
      )}

      {(dados?.regras ?? []).length > 0 && (
        <ul className="divide-y divide-zinc-100 border-t border-zinc-100 dark:divide-border/50 dark:border-border">
          {dados!.regras.map((r) => (
            <li key={r.id} className="flex items-center gap-3 px-4 py-2.5">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-zinc-900 dark:text-foreground">{r.nome}</p>
                <p className="text-[11px] text-zinc-500 dark:text-muted-foreground">{resumo(regraEfetiva(padrao, r))}</p>
              </div>
              {pode && (
                <>
                  <button type="button" onClick={() => setEditando({ ...VAZIA, ...r })} aria-label={`Editar regra de ${r.nome}`} className="rounded p-1.5 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700 dark:hover:bg-muted">
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => window.confirm(`Tirar a regra de ${r.nome}? Ele volta ao padrão.`) && remover.mutate({ clientOrganizationId: r.clientOrganizationId })}
                    aria-label={`Tirar regra de ${r.nome}`}
                    className="rounded p-1.5 text-zinc-400 hover:bg-zinc-100 hover:text-red-600 dark:hover:bg-muted"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
